import { Zap, FileText, Scale, FileSearch } from "lucide-react";
import type { ResponseMode } from "@/lib/api";

interface ModeOption {
  value: ResponseMode;
  label: string;
  description: string;
  icon: typeof Zap;
  warning?: string;
}

const MODES: ModeOption[] = [
  {
    value: "quick",
    label: "Rapide",
    description: "Réponse courte, syllogisme + source principale.",
    icon: Zap,
  },
  {
    value: "legal_note",
    label: "Note juridique",
    description:
      "Markdown structuré — MAJEURE / MINEURE / CONCLUSION + sources détaillées.",
    icon: FileText,
  },
  {
    value: "counter_argument",
    label: "Contradiction",
    description:
      "Pro-demandeur ET pro-défendeur, avec exceptions et limites.",
    icon: Scale,
    warning:
      "Mode contradiction — à utiliser avec discernement, n'engage pas l'assistant.",
  },
  {
    value: "audit",
    label: "Audit",
    description: "Réponse minimale + traces complètes (chunks, claims, audit_id).",
    icon: FileSearch,
  },
];

interface ModeSelectorProps {
  value: ResponseMode;
  onChange: (mode: ResponseMode) => void;
  disabled?: boolean;
}

export function ModeSelector({ value, onChange, disabled }: ModeSelectorProps) {
  const current = MODES.find((m) => m.value === value);

  return (
    <div className="space-y-2">
      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-2"
        role="radiogroup"
        aria-label="Mode de réponse de l'assistant IA"
      >
        {MODES.map((mode) => {
          const Icon = mode.icon;
          const active = mode.value === value;
          return (
            <button
              key={mode.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={`${mode.label} — ${mode.description}`}
              onClick={() => !disabled && onChange(mode.value)}
              disabled={disabled}
              className="text-xs px-3 py-2 rounded-md border transition-colors text-left flex flex-col gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                borderColor: active
                  ? "var(--ai-violet)"
                  : "var(--graphite-600)",
                background: active
                  ? "color-mix(in oklch, var(--ai-violet) 12%, transparent)"
                  : "var(--graphite-700)",
                color: "var(--arbitre-slate-100)",
              }}
            >
              <span className="flex items-center gap-1.5 font-medium">
                <Icon size={12} />
                {mode.label}
              </span>
            </button>
          );
        })}
      </div>
      {current && (
        <p
          className="text-[11px]"
          style={{ color: "var(--arbitre-slate-300)", lineHeight: 1.5 }}
        >
          {current.description}
        </p>
      )}
      {current?.warning && (
        <p
          className="text-[11px] flex items-start gap-1 px-2 py-1 rounded"
          style={{
            color: "var(--alert-orange)",
            background:
              "color-mix(in oklch, var(--alert-orange) 10%, transparent)",
          }}
        >
          ⚠️ {current.warning}
        </p>
      )}
    </div>
  );
}
